import { huddleCaptureConstraints, type HuddleDeviceSource } from './HuddleMedia';
import { applyHuddleCameraPreferences, configureHuddleCamera, readHuddleCameraDetails, type HuddleCameraDetails, type HuddleCameraPreferences } from './HuddleCamera';

interface PreviewDevice {
  busy: boolean;
  stream?: MediaStream;
  error?: string;
}
export interface HuddlePreviewSnapshot {
  microphone: PreviewDevice;
  camera: PreviewDevice;
  level: number;
  devices: MediaDeviceInfo[];
  devicesError?: string;
  cameraDetails?: HuddleCameraDetails;
}
export const EMPTY_PREVIEW: HuddlePreviewSnapshot = { microphone: { busy: false }, camera: { busy: false }, level: 0, devices: [] };
interface MicrophoneMeter {
  context: AudioContext;
  source?: MediaStreamAudioSourceNode;
  analyser?: AnalyserNode;
  timer?: ReturnType<typeof setInterval>;
}

/** Owns local-only preview devices; it has no signalling or publication path. */
export class HuddlePreview {
  private disposed = false;
  private versions: Record<HuddleDeviceSource, number> = { microphone: 0, camera: 0 };
  private enumeration = 0;
  private meter?: MicrophoneMeter;
  private cameraDeviceKey?: string;
  private cameraTrackDeviceId?: string;
  private cameraUpdates: Promise<unknown> = Promise.resolve();
  private cameraAdjustment = 0;
  private cameraConfigurationCleanup?: () => void;
  private state: HuddlePreviewSnapshot = { ...EMPTY_PREVIEW, microphone: { busy: false }, camera: { busy: false } };
  constructor(private readonly changed: (state: HuddlePreviewSnapshot) => void) {}
  private emit(): void { if (!this.disposed) this.changed({ ...this.state }); }

  async refreshDevices(): Promise<void> {
    const version = ++this.enumeration;
    try {
      if (!navigator.mediaDevices?.enumerateDevices) throw new DOMException('Unavailable', 'NotSupportedError');
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (this.disposed || this.enumeration !== version) return;
      this.state.devices = devices.filter(device => device.kind === 'audioinput' || device.kind === 'videoinput' || device.kind === 'audiooutput');
      this.state.devicesError = undefined;
    } catch {
      if (this.disposed || this.enumeration !== version) return;
      this.state.devicesError = 'Could not list devices. You can still test your default microphone or camera.';
    }
    this.emit();
  }

  async start(source: HuddleDeviceSource, deviceId?: string, cameraPreferences?: HuddleCameraPreferences): Promise<void> {
    if (this.disposed) return;
    if (source === 'camera') {
      const deviceKey = deviceId ?? 'default';
      if (this.cameraDeviceKey !== undefined && this.cameraDeviceKey !== deviceKey && cameraPreferences) cameraPreferences = { ...cameraPreferences, controls: {} };
      this.cameraDeviceKey = deviceKey;
    }
    this.stop(source);
    const version = this.versions[source];
    this.state[source] = { busy: true }; this.emit();
    let stream: MediaStream | undefined;
    let requestedCamera: HuddleCameraPreferences | undefined;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new DOMException('Unavailable', 'NotSupportedError');
      if (source === 'microphone') {
        // Construct/resume from the explicit click, retaining Safari user activation.
        const Audio = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Audio) throw new DOMException('Unavailable', 'NotSupportedError');
        const meter = { context: new Audio() };
        this.meter = meter;
        void meter.context.resume().catch(() => {
          if (!this.disposed && this.versions[source] === version) {
            this.stop(source); this.state[source] = { busy: false, error: 'AudioContextError' }; this.emit();
          }
        });
      }
      stream = await navigator.mediaDevices.getUserMedia(huddleCaptureConstraints(source, deviceId, cameraPreferences));
      if (this.disposed || version !== this.versions[source]) { stream.getTracks().forEach(track => track.stop()); return; }
      const track = source === 'microphone' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
      if (!track || track.readyState === 'ended') throw new DOMException('Unavailable', 'NotFoundError');
      stream.getTracks().filter(extra => extra !== track).forEach(extra => extra.stop());
      this.state[source] = { busy: false, stream: new MediaStream([track]) };
      if (source === 'camera') {
        let actualDeviceId: string | undefined;
        try { actualDeviceId = track.getSettings?.().deviceId; } catch { /* Device identity is not exposed in every browser. */ }
        if (actualDeviceId && this.cameraTrackDeviceId && actualDeviceId !== this.cameraTrackDeviceId && cameraPreferences) cameraPreferences = { ...cameraPreferences, controls: {} };
        this.cameraTrackDeviceId = actualDeviceId;
        const details = readHuddleCameraDetails(track, cameraPreferences);
        requestedCamera = cameraPreferences ?? {};
        this.state.cameraDetails = { ...details, preferences: { ...details.preferences, controls: {} } };
        const configured = () => {
          if (this.disposed || version !== this.versions.camera || this.state.camera.stream?.getVideoTracks()[0] !== track || track.readyState === 'ended') return;
          const previous = this.state.cameraDetails;
          const updated = readHuddleCameraDetails(track, previous?.preferences);
          if (previous && !previous.applying) {
            const wasFraming = previous.settings.effects?.faceFraming;
            const framing = updated.settings.effects?.faceFraming;
            if (wasFraming !== undefined && framing !== undefined && wasFraming !== framing) updated.preferences.effects = { ...updated.preferences.effects, faceFraming: framing };
            const wasFocus = previous.settings.modes.focusMode;
            const focus = updated.settings.modes.focusMode;
            if (wasFocus && focus && wasFocus !== focus && updated.capabilities.modes.focusMode?.includes(focus)) {
              updated.preferences.controls = { ...updated.preferences.controls, focusMode: focus };
              if (focus !== 'manual') delete updated.preferences.controls.focusDistance;
            }
          }
          this.state.cameraDetails = { ...updated, applying: previous?.applying ?? false, error: previous?.error };
          this.emit();
        };
        track.addEventListener('configurationchange', configured);
        this.cameraConfigurationCleanup = () => track.removeEventListener('configurationchange', configured);
      }
      track.addEventListener('ended', () => {
        if (this.state[source].stream?.getTracks()[0] !== track) return;
        this.stop(source); this.state[source] = { busy: false, error: 'DeviceDisconnected' }; this.emit();
      }, { once: true });
      if (source === 'microphone') this.startMeter(this.state.microphone.stream!, version);
      this.emit();
      void this.refreshDevices();
      if (requestedCamera) await this.adjustCamera(requestedCamera, true);
    } catch (error) {
      stream?.getTracks().forEach(track => track.stop());
      if (this.disposed || version !== this.versions[source]) return;
      this.stop(source);
      this.state[source] = { busy: false, error: error instanceof Error ? error.name : 'UnknownError' }; this.emit();
    }
  }

  /** Serial adjustments belong to the current permitted track; settings never start capture. */
  async updateCamera(preferences: HuddleCameraPreferences): Promise<HuddleCameraDetails | undefined> {
    return this.adjustCamera(preferences);
  }

  private async adjustCamera(preferences: HuddleCameraPreferences, initial = false): Promise<HuddleCameraDetails | undefined> {
    const track = this.state.camera.stream?.getVideoTracks()[0];
    if (!track || this.disposed || track.readyState === 'ended') return undefined;
    const version = this.versions.camera;
    const adjustment = ++this.cameraAdjustment;
    const current = () => !this.disposed && version === this.versions.camera && this.state.camera.stream?.getVideoTracks()[0] === track && track.readyState !== 'ended';
    this.state.cameraDetails = { ...readHuddleCameraDetails(track, this.state.cameraDetails?.preferences), applying: true };
    this.emit();
    const task = this.cameraUpdates.then(async () => {
      if (!current()) return undefined;
      const previous = this.state.cameraDetails?.preferences ?? {};
      try {
        const details = await (initial ? configureHuddleCamera(track, preferences) : applyHuddleCameraPreferences(track, preferences));
        if (!current()) return undefined;
        this.state.cameraDetails = { ...details, applying: adjustment !== this.cameraAdjustment };
        this.emit();
        return adjustment === this.cameraAdjustment ? details : undefined;
      } catch (error) {
        if (!current()) return undefined;
        this.state.cameraDetails = { ...readHuddleCameraDetails(track, previous), applying: adjustment !== this.cameraAdjustment, error: error instanceof Error ? error.name : 'UnknownError' };
        this.emit();
        return undefined;
      }
    });
    this.cameraUpdates = task;
    return task;
  }

  private startMeter(stream: MediaStream, version: number): void {
    const meter = this.meter;
    if (!meter) throw new DOMException('Unavailable', 'NotSupportedError');
    meter.source = meter.context.createMediaStreamSource(stream);
    meter.analyser = meter.context.createAnalyser(); meter.analyser.fftSize = 512;
    meter.source.connect(meter.analyser); // No destination connection: microphone tests never play through speakers.
    const values = new Float32Array(meter.analyser.fftSize);
    meter.timer = setInterval(() => {
      if (this.disposed || this.versions.microphone !== version || this.meter !== meter) return;
      try {
        meter.analyser!.getFloatTimeDomainData(values);
        const rms = Math.sqrt(values.reduce((sum, value) => sum + value * value, 0) / values.length);
        this.state.level = Math.min(100, Math.round(rms * 400)); this.emit();
      } catch {
        this.stop('microphone'); this.state.microphone = { busy: false, error: 'AudioContextError' }; this.emit();
      }
    }, 100);
  }

  stop(source: HuddleDeviceSource): void {
    this.versions[source] += 1;
    const stream = this.state[source].stream;
    this.state[source] = { busy: false };
    if (source === 'camera') {
      this.cameraConfigurationCleanup?.();
      this.cameraConfigurationCleanup = undefined;
      this.state.cameraDetails = undefined;
      this.cameraAdjustment += 1;
      this.cameraUpdates = Promise.resolve();
    }
    stream?.getTracks().forEach(track => track.stop());
    if (source === 'microphone') {
      const meter = this.meter; this.meter = undefined;
      if (meter) {
        clearInterval(meter.timer);
        meter.source?.disconnect(); meter.analyser?.disconnect();
        void meter.context.close().catch(() => {});
      }
      this.state.level = 0;
    }
    this.emit();
  }
  stopAll(): void { this.stop('microphone'); this.stop('camera'); }
  dispose(): void { this.disposed = true; this.enumeration += 1; this.stopAll(); }
}
